import React, { useEffect, useState } from 'react';
import pick from 'lodash/pick';
import times from 'lodash/times';
import moment from 'moment';
import { saveAs } from 'file-saver';
import formatAsCurrency from '../../../CommonUtils/formatAsCurrency';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { useCampaignDetailsQuery } from '../../api/useCampaignDetailsQuery';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Shimmer } from '../../../CommonComponents/OPL/Shimmer/Shimmer';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { TableRow } from '../../Campaigns.styles';
import { CampaignDetailsLoading } from './CampaignDetails.loading';
import { useGrantAccessToClientMutation } from '../../api/useGrantAccessToClientMutation';

const buildCsv = clients =>
  [
    [
      'Name',
      'First Engagement',
      'Income',
      'Total Assets',
      'Email',
      'Engagement Date',
      'Completion Date',
    ].join(','),
    ...clients.map(
      client =>
        `${Object.values(
          pick(client, [
            'name',
            'firstEngagement',
            'income',
            'totalAssets',
            'email',
            'engagementDate',
            'completionDate',
          ]),
        ).join(',')}`,
    ),
  ].join('\n');

const COLUMNS_COUNT = 7;

interface Props {
  campaignId: string;
  onClose: () => void;
}

export const CampaignDetails = ({ campaignId, onClose }: Props) => {
  const [grantedClientId, setGrantedClientId] = useState<number | null>(null);

  const { data: campaignDetails, isFetching, isError } = useCampaignDetailsQuery({ campaignId });

  const {
    mutateAsync: grantAccessToClient,
    data: grantAccessToClientData,
    isLoading: isGrantAccessToClientLoading,
  } = useGrantAccessToClientMutation();

  useEffect(() => {
    if (grantAccessToClientData?.isActive) {
      ADV.util.Router.go2('ConsumerOverview', {
        clientId: grantedClientId,
      });
    }
  }, [grantAccessToClientData]);

  return (
    <Modal
      isOpen={!!campaignId}
      onRequestClose={onClose}
      css="max-width: fit-content;"
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'campaign-detail--modal-header' }}
    >
      <ModalHeader as="h2" id="campaign-detail--modal-header">
        Campaign Responses
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody css="padding: 0">
        {isFetching ? (
          <CampaignDetailsLoading />
        ) : (
          <HtmlTable mb="24" variant="embedded">
            <table>
              <thead>
                <tr>
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Name
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="center">
                      First Engagement
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="right">
                      Income
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="right">
                      Total Assets
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Email
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Engagement Date
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Completion Date
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isError ? (
                  <tr>
                    <td colSpan={COLUMNS_COUNT}>
                      <ErrorState mx="auto" my="56" />
                    </td>
                  </tr>
                ) : (
                  campaignDetails &&
                  (campaignDetails.length > 0 ? (
                    campaignDetails.map(client => (
                      <TableRow
                        style={{
                          cursor: client.isDeleted ? 'default' : 'pointer',
                        }}
                        key={client.clientId}
                        onClick={() => {
                          if (!client.isDeleted) {
                            setGrantedClientId(client.clientId);
                            grantAccessToClient({ clientId: client.clientId, campaignId });
                          }
                        }}
                      >
                        {isGrantAccessToClientLoading && grantedClientId === client.clientId ? (
                          times(7).map(index => (
                            <td key={index}>
                              <Shimmer width="100%" height="20px" />
                            </td>
                          ))
                        ) : (
                          <>
                            <td style={{ maxWidth: 200 }}>
                              <Text isTruncated>{client.name}</Text>
                            </td>
                            <td>
                              {client.firstEngagement && (
                                <Box display="flex" justifyContent="center">
                                  <Icon className="far fa-check" color="success.500" />
                                </Box>
                              )}
                            </td>
                            <td>
                              <Text variant="tabular" textAlign="right">
                                {formatAsCurrency(client.income)}
                              </Text>
                            </td>
                            <td>
                              <Text variant="tabular" textAlign="right">
                                {formatAsCurrency(client.totalAssets)}
                              </Text>
                            </td>
                            <td>
                              <Text>{client.email}</Text>
                            </td>
                            <td>
                              <Text>{moment(client.engagementDate).format('MM/DD/YYYY')}</Text>
                            </td>
                            <td>
                              <Text>
                                {client.completionDate &&
                                  moment(client.completionDate).format('MM/DD/YYYY')}
                              </Text>
                            </td>
                          </>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <Box display="flex" flexDirection="column" alignItems="center" p={120}>
                          <Icon className="far fa-envelope" fontSize="h2" color="neutral.500" />

                          <Text textAlign="center" fontSize="body" color="neutral.500" mt="8">
                            No responses for this campaign
                          </Text>
                        </Box>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </HtmlTable>
        )}
      </ModalBody>

      <ModalFooter>
        {campaignDetails && campaignDetails.length > 0 && (
          <Button
            leftIcon="far fa-arrow-circle-down"
            variant="subtleActive"
            isIconButton
            onClick={() => {
              const blob = new Blob([buildCsv(campaignDetails)], {
                type: 'text/csv;charset=utf-8',
              });

              saveAs(blob, 'Responses.csv');
            }}
            mr="auto"
          >
            Download Template
          </Button>
        )}

        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
